import { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Fade,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api/axios";
import { storeClientTokenForCase } from "../../api/axios";
import FirmaContent from "../ViewSections/AccordionSections/FirmaContent";
import {
  b64DecodeUnicode,
  formatToTenDigits,
} from "../../Components/helpers/Helpers";
import {
  ClientContext,
  PermissionContext,
} from "../../Components/Context/Context";
import fidelitis2 from "../../assets/fidelitis2.png";

import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import NotFoundPage from "../../Components/Common/NotFoundPage";
import { Mail } from "lucide-react";
import { ArrowBackIos, ArrowForwardIos, Logout } from "@mui/icons-material";
import FormFI from "../Components/FormFI";
import { normalizeExpedienteId } from "../../utils/expediente";

const pageContainer = {
  minHeight: "100vh",
  p: { xs: 0.75, sm: 2.5, md: 3 },
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "left",
};

const glassCard = {
  width: { xs: "auto", sm: "100%" },
  borderRadius: { xs: "8px", sm: "10px" },
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(6px)",
  boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
};

const sectionBox = {
  width: { xs: "auto", sm: "100%" },
  maxWidth: { xs: "100%", md: "80%" },
  mx: "auto",
  p: { xs: 1, sm: 2, md: 4 },
  mb: { xs: 1.5, sm: 3 },
  display: "grid",
  ...glassCard,
};

const actionButton = {
  borderRadius: { xs: "8px", sm: "10px" },
  px: { xs: 2, sm: 3 },
  py: { xs: 0.8, sm: 1 },
  fontWeight: 600,
  fontSize: { xs: "12px", sm: "14px" },
};

const otpInput = {
  width: { xs: 40, sm: 50 },
  "& input": {
    textAlign: "center",
    fontSize: { xs: "18px", sm: "22px" },
    fontWeight: "bold",
    p: { xs: "8px", sm: "10px" },
  },
};

const expedienteNumberLoading = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.4,
  ml: 0.6,
  color: "text.secondary",
  fontSize: { xs: "0.9rem", sm: "1rem" },
  "& .dot": {
    width: 5,
    height: 5,
    borderRadius: "50%",
    backgroundColor: "currentColor",
    opacity: 0.35,
    animation: "expediente-dot-bounce 1.2s infinite ease-in-out",
  },
  "& .dot:nth-of-type(2)": {
    animationDelay: "0.15s",
  },
  "& .dot:nth-of-type(3)": {
    animationDelay: "0.3s",
  },
  "@keyframes expediente-dot-bounce": {
    "0%, 80%, 100%": {
      opacity: 0.35,
      transform: "translateY(0)",
    },
    "40%": {
      opacity: 1,
      transform: "translateY(-3px)",
    },
  },
};

// Si está en true, al pulsar Backspace/Delete en un recuadro OTP vacío,
// el foco salta al recuadro anterior para editar rápido.
const OTP_BACKSPACE_JUMP_ON_EMPTY = true;

const resolveCaseId = (searchParams) => {
  const directId = searchParams.get("id");
  const normalizedId = normalizeExpedienteId(directId);

  return normalizedId || null;
};

const sanitizeCaseTypeLabel = (value) => {
  const text = (value ?? "").toString().trim();
  if (!text) return "";

  const openCount = (text.match(/\(/g) || []).length;
  const closeCount = (text.match(/\)/g) || []).length;

  // Solo limpia si hay cierre sobrante al final.
  if (closeCount > openCount && /\)\s*$/.test(text)) {
    return text.replace(/\s*\)+\s*$/, "").trim();
  }

  return text;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const message = responseData?.message;

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  if (Array.isArray(message) && message.length > 0) {
    return message.join(" ");
  }

  if (message && typeof message === "object") {
    const firstNestedMessage = Object.values(message)
      .flat()
      .find((value) => typeof value === "string" && value.trim());

    if (firstNestedMessage) {
      return firstNestedMessage;
    }
  }

  const errors = responseData?.errors;
  if (errors && typeof errors === "object") {
    const firstNestedError = Object.values(errors)
      .flat()
      .find((value) => typeof value === "string" && value.trim());

    if (firstNestedError) {
      return firstNestedError;
    }
  }

  return fallbackMessage;
};

const ClientSign = () => {
  const { clientIsLoggedIn, clientIsLoading, userData, logout, fetchUserData } =
    useContext(ClientContext);

  const { user } = userData ?? {};
  const { can } = useContext(PermissionContext);
  const canSign = can("cases", "signcontract");

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const case_id = resolveCaseId(searchParams);

  const showMissingCaseAlert = async () => {
    await Swal.fire({
      icon: "warning",
      title: "Enlace inválido",
      text: "No pudimos identificar el expediente. Abre nuevamente el enlace completo recibido por correo.",
      confirmButtonColor: "#1ab394",
    });
  };

  const [canFillForm, setCanFillForm] = useState(false);
  const [activeTask, setActiveTask] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showCompletionStep, setShowCompletionStep] = useState(false);

  // Dígitos del código OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  // Correo ingresado por el usuario
  const [clientEmail, setClientEmail] = useState("");
  const [dataCase, setDataCase] = useState([]);
  const [caseContract, setCaseContract] = useState({});
  const [acceptedContract, setAcceptedContract] = useState("");
  const [updatedCase, setUpdatedCase] = useState("");
  const [forceRender, setForceRender] = useState(0);
  const [isCaseNumberLoading, setIsCaseNumberLoading] = useState(false);

  const isCaseLoaded = !!dataCase?.id;
  const isUserLoaded = !!user?.id;

  const [bgLoaded, setBgLoaded] = useState(false);

  const hasFI = dataCase?.completeFlow?.some(
    (flow) => flow.status_name?.toLowerCase() === "formulario fi",
  );
  const flowById = new Map(
    (dataCase?.completeFlow ?? []).map((flow) => [
      Number(flow?.id),
      Number(flow?.order) || 0,
    ]),
  );
  const currentFlowOrder = flowById.get(Number(dataCase?.case_status)) || 0;
  const fiFlow = (dataCase?.completeFlow ?? []).find(
    (flow) => flow.status_name?.toLowerCase() === "formulario fi",
  );
  const fiFlowOrder = Number(fiFlow?.order) || 0;
  const steps = hasFI
    ? ["Firma del contrato", "Formulario FI", "Proceso completado"]
    : ["Firma del contrato", "Proceso completado"];
  const finalStepIndex = steps.length - 1;
  const lastFlowId =
    dataCase?.completeFlow?.[dataCase.completeFlow.length - 1]?.id;
  const finalFlowOrder = flowById.get(Number(lastFlowId)) || 0;
  const hasSignedContract = Boolean(acceptedContract);
  const hasCompletedProcess =
    finalFlowOrder > 0 && currentFlowOrder >= finalFlowOrder;
  const completionReady = showCompletionStep || hasCompletedProcess;
  const canAccessFIStep =
    hasFI &&
    fiFlowOrder > 0 &&
    (currentFlowOrder >= fiFlowOrder || hasSignedContract);
  const maxAccessibleStep = completionReady
    ? finalStepIndex
    : canAccessFIStep
      ? 1
      : 0;

  const getCaseSign = useCallback(async () => {
    setIsCaseNumberLoading(true);

    if (!case_id) {
      await showMissingCaseAlert();
      setIsCaseNumberLoading(false);
      return;
    }

    // PRINCIPAL INFO
    const url = `${process.env.REACT_APP_API_URL}`;

    try {
      const response = await api.post(url + "/sign_contract", { id: case_id });

      const caseData = response?.data?.case?.[0];
      if (!caseData) {
        throw new Error("No se encontró información del expediente.");
      }

      const {
        id,
        case_type_name,
        case_status,
        company_name,
        clients_relation,
        case_contract_relation,
        cases_flow_relation,
        last_task,
        previous_case_statuses_relation,
        cases_notes_relation,
        cases_alerts_relation,
        peritaje,
        apud_acta,
        full_case_status_name,
        full_client_name,
        full_lawyer_name,
        full_office_name,
        lawyers_relation,
        company_relation,
        trial_date,
        last_update,
        client_id,
        transfer_id,
      } = caseData;

      const formattedNumber = formatToTenDigits(id);
      const sortedStatuses = [...(previous_case_statuses_relation ?? [])].sort(
        (a, b) => a.order - b.order,
      );

      setDataCase({
        id,
        case_type_name: sanitizeCaseTypeLabel(case_type_name),
        case_id: formattedNumber,
        alerts: cases_alerts_relation,
        client_id,
        notes: cases_notes_relation,
        peritaje,
        apud_acta,
        status_name: full_case_status_name,
        task: last_task,
        case_status,
        tasks: response?.data?.case_tasks ?? [],
        completeFlow: sortedStatuses,
        flow: cases_flow_relation,
        last_update,
        transfer_id,
        trial_date,
        clients_relation,
        full_client_name,
        full_lawyer_name,
        full_office_name,
        lawyers_relation,
        company_relation,
        company_name,
      });

      setClientEmail((clients_relation?.email ?? "").trim());

      const caseTasks = response?.data?.case_tasks ?? [];
      const fiTask = caseTasks.find(
        (task) =>
          task?.cases_flow_relation?.status_name?.toLowerCase().trim() ===
          "formulario fi",
      );
      setCanFillForm(!!fiTask);
      setActiveTask(fiTask || false);

      // Reemplazo de tags en contrato
      const text = case_contract_relation?.contract ?? "";
      const htmlText = text.replace(/\n/g, "<br>");
      const tagValues = {
        "#NOMBRE#": `<b>${clients_relation?.name}</b>`,
        "#APELLIDOS#": `<b>${clients_relation?.surname}</b>`,
        "#DIRECCION#": `<b>${clients_relation?.address}</b>`,
        "#PROVINCIA#": `<b>${clients_relation?.province}</b>`,
        "#DNI#": `<b>${clients_relation?.nif}</b>`,
      };
      const resultReplaceTags = htmlText.replace(
        /#(\w+)#/g,
        (_, key) => tagValues[`#${key}#`] || key,
      );

      // Firma aceptada
      let signCurrentStatus;
      let latestAcceptedContract = "";

      if (caseTasks.length > 0) {
        caseTasks.forEach((element) => {
          if (element?.config && "firma" in element?.config) {
            const decodedContract = b64DecodeUnicode(element?.config.firma);

            if (decodedContract) {
              latestAcceptedContract = decodedContract;
            }

            signCurrentStatus = element?.config;
          }
        });

        if (latestAcceptedContract) {
          setAcceptedContract(latestAcceptedContract);
        }
      }

      const signedContractHtml = latestAcceptedContract || "";
      const baseContractHtml = case_contract_relation?.contract || signedContractHtml || "";

      setCaseContract({
        firmaUsuario: signCurrentStatus,
        contract: signedContractHtml || resultReplaceTags || baseContractHtml,
        contractTitle:
          case_contract_relation?.contract_title ||
          (signedContractHtml ? "Contrato guardado" : "Contrato"),
        contractAcceptData: {
          client_name: full_client_name,
          client_nif: clients_relation?.nif,
          company_name:
            case_contract_relation?.company_relation?.company_name ||
            company_name ||
            "",
          client_ip: response?.data?.ip ? response.data.ip : "0.0.0.0",
        },
      });
    } catch (error) {
      // Log completo en navegador
      if (error && error.response) {
        console.error("[ClientSign] Error en getCaseSign:", {
          message: error.message,
          response: error.response,
          data: error.response.data,
          status: error.response.status,
          headers: error.response.headers,
        });
      } else {
        console.error("[ClientSign] Error en getCaseSign:", error);
      }
    } finally {
      Swal.close();
      setIsCaseNumberLoading(false);
    }
    // PRINCIPAL INFO
  }, [case_id]);

  const handleGoBack = () => {
    navigate("/", { replace: true });
  };

  // Maneja los cambios en cada input del OTP
  const handleOtpChange = (value, index) => {
    const digits = (value ?? "").replace(/\D/g, "");
    if (!digits) {
      const nextOtp = [...otp];
      nextOtp[index] = "";
      setOtp(nextOtp);
      return;
    }

    const nextOtp = [...otp];
    nextOtp[index] = digits[0];
    setOtp(nextOtp);

    // Avanza automáticamente al siguiente input cuando se escribe un dígito.
    if (index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (event, index) => {
    const key = event.key;

    if (key === "Backspace" || key === "Delete") {
      // Si el input actual tiene valor, solo limpia el actual y mantiene foco.
      if (otp[index]) {
        event.preventDefault();
        const nextOtp = [...otp];
        nextOtp[index] = "";
        setOtp(nextOtp);
        return;
      }

      // Si está vacío, el salto al anterior es configurable.
      if (OTP_BACKSPACE_JUMP_ON_EMPTY && index > 0) {
        event.preventDefault();
        document.getElementById(`otp-${index - 1}`)?.focus();
      }

      return;
    }

    if (key === "ArrowLeft") {
      event.preventDefault();
      if (index > 0) {
        document.getElementById(`otp-${index - 1}`)?.focus();
      }
      return;
    }

    if (key === "ArrowRight") {
      event.preventDefault();
      if (index < otp.length - 1) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
      return;
    }

    if (key === " " || key === "Spacebar") {
      event.preventDefault();
      return;
    }
  };

  const handleOtpPaste = (event, startIndex) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") ?? "";
    const digits = pasted.replace(/\D/g, "");
    if (!digits) {
      return;
    }

    const nextOtp = [...otp];
    let pointer = startIndex;

    for (const digit of digits) {
      if (pointer >= nextOtp.length) {
        break;
      }
      nextOtp[pointer] = digit;
      pointer += 1;
    }

    setOtp(nextOtp);

    const focusIndex = Math.min(pointer, nextOtp.length - 1);
    document.getElementById(`otp-${focusIndex}`)?.focus();
  };

  const nextTask = async (
    config,
    task_status,
    status_desicion,
    message,
    anotherTask,
  ) => {
    let ultimo;
    try {
      const flowById = new Map(
        (dataCase?.completeFlow || []).map((flow) => [Number(flow?.id), flow]),
      );

      const firstPendingTaskId = (dataCase?.tasks || [])
        .filter((task) => Number(task?.status) === 0)
        .map((task) => ({
          taskId: Number(task?.task_id),
          order: Number(flowById.get(Number(task?.task_id))?.order || 0),
        }))
        .filter((task) => task.order > 0)
        .sort((a, b) => a.order - b.order)[0]?.taskId;

      const currentTaskId = Number(
        anotherTask || firstPendingTaskId || dataCase?.task?.task_id || 0,
      );

      if (status_desicion === "lastone") {
        ultimo =
          dataCase?.completeFlow?.[(dataCase.completeFlow.length ?? 1) - 1];
      }

      Swal.fire({
        title: "Cargando...",
        html: '<div class="swal-loader"></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      let nextId;
      let nextTime;

      const flow = dataCase?.completeFlow ?? [];
      for (let i = 0; i < flow.length; i++) {
        if (currentTaskId === Number(flow[i]?.id)) {
          nextId = flow[i + 1]?.id;
          nextTime = flow[i + 1]?.status_user_max_delay;
        }
      }

      const hasNextTask = !!nextId;

      if (status_desicion === "next" && !hasNextTask) {
        Swal.close();
        Swal.fire({
          title: "Flujo completado",
          text: "No existen más tareas en este caso.",
          icon: "info",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Aceptar",
        });
        return;
      }

      //  Datos para actualizar la tarea
      const payload = {
        case_status: ultimo?.id
          ? ultimo.id
          : !status_desicion || status_desicion === "next"
            ? nextId
            : currentTaskId,
        case_id: case_id,
        task_id: ultimo?.id ? ultimo.id : nextId,
        status: task_status,
        config: JSON.stringify(config),
        next_config: JSON.stringify({}),
        current_task: currentTaskId,
        update_desicion: status_desicion,
        status_user_max_delay: ultimo?.id ? ultimo.id : nextTime,
        client_name: dataCase?.full_client_name,
      };

      // Si hay mensaje, lo agregamos
      if (message !== "") {
        payload.message = JSON.stringify(message);
      }

      // Ahora generamos el FormData
      const formDataTask = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        formDataTask.append(key, value);
      });

      const url = `${process.env.REACT_APP_API_URL}`;

      await api.post(url + "/nexttask", formDataTask);

      const shouldShowFinalStep =
        Boolean(config?.form_fi) || (Boolean(config?.firma) && !hasFI);

      if (shouldShowFinalStep) {
        setShowCompletionStep(true);
        setActiveStep(finalStepIndex);
      }

      Swal.close();

      const isFISubmission = Boolean(config?.form_fi);
      const successTitle = isFISubmission
        ? "FI registrado"
        : "Firma registrada";
      const successText = isFISubmission
        ? "Su formulario FI ha sido registrado exitosamente"
        : "Su firma ha sido registrada exitosamente";

      Swal.fire({
        title: successTitle,
        text: successText,
        icon: "success",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
        allowEscapeKey: false,
        allowOutsideClick: false,
      }).then((result) => {
        setUpdatedCase((prev) => !prev);
        setForceRender((prev) => prev + 1);
      });
    } catch (err) {
      Swal.close();

      //   Detectamos tipo de error para dar mejor feedback
      let errorMessage = "Ocurrió un error inesperado. Intenta nuevamente.";

      if (err.response) {
        // Error del servidor con respuesta
        errorMessage = err.response.data?.message || "Error en el servidor.";
      } else if (err.request) {
        // El servidor no respondió
        errorMessage = "No hay conexión con el servidor. Revisa tu red.";
      } else {
        // Error en la configuración de la petición
        errorMessage = `Error: ${err.message}`;
      }

      console.error("   Error en nextTask:", err);

      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "Cerrar",
      });
    }
  };

  // Procesa la verificación de información
  const handleClientSignInformation = async () => {
    const otpCode = otp.join("");
    if (!clientEmail) {
      Swal.fire({
        icon: "warning",
        title: "Correo no disponible",
        text: "No se encontró el correo del cliente asociado al expediente.",
      });
      return;
    }
    if (otp.every((d) => d === "")) {
      Swal.fire({
        icon: "warning",
        title: "Código requerido",
        text: "Por favor ingresa el código de verificación.",
      });
      return;
    }
    if (otpCode.length < 6) {
      Swal.fire({
        icon: "warning",
        title: "Código incompleto",
        text: "El código de verificación debe tener 6 dígitos.",
      });
      return;
    }
    showLoading("Verificando código...");
    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/verify-client-otp`,
        {
          otp: otpCode,
          case_id,
        },
      );
      const temporalTokenFromResponse = res?.data?.temporal_token;
      if (temporalTokenFromResponse && case_id) {
        storeClientTokenForCase(case_id, temporalTokenFromResponse);
      }
      await Swal.fire({
        icon: "success",
        title: "Código válido",
        allowOutsideClick: false,
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        text: res?.data?.message || "Validación completada.",
      });
      await fetchUserData();
      return;
    } catch (e) {
      const msg = getApiErrorMessage(e, "El código ingresado no es válido.");

      Swal.fire({
        icon: "error",
        title: "Código inválido",
        text: msg,
      });
      return;
    }
  };

  const handleResendCode = async () => {
    if (!case_id) {
      await showMissingCaseAlert();
      return;
    }
    if (!dataCase?.clients_relation?.email) {
      await Swal.fire({
        icon: "warning",
        title: "Correo no disponible",
        text: "No se encontró el correo del cliente asociado al expediente.",
      });
      return;
    }
    // Confirmación de reenvío
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: "¿Reenviar código?",
      text: `Se enviará un nuevo código de verificación al correo del cliente: ${dataCase?.clients_relation?.email}`,
      showCancelButton: true,
      confirmButtonText: "Sí, reenviar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    });
    if (!isConfirmed) return;
    // Loading envío de código
    Swal.fire({
      title: "Enviando código...",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/send-otp-client`, {
        case_id: Number(case_id),
      });
      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Código enviado",
        text: `El código ha sido enviado al correo del cliente: ${clientEmail}`,
        confirmButtonColor: "#1ab394",
      });
    } catch (error) {
      Swal.close();

      await Swal.fire({
        icon: "error",
        title: "Error al enviar el código",
        text: getApiErrorMessage(
          error,
          "No se pudo enviar el código. Intenta nuevamente.",
        ),
      });
    }
  };

  // SweetAlert de carga
  const showLoading = (text = "Procesando...") => {
    Swal.fire({
      title: text,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
  };

  const cerrarSesion = () => {
    Swal.fire({
      title: "Cerrar sesión",
      icon: "warning",
      html: `<b>¿Deseas continuar?</b>`,
      showDenyButton: "Cancelar.",
      confirmButtonText: "Continuar",
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };

  // Activa la pantalla cuando carga el componente
  useEffect(() => {
    // if (clientIsLoggedIn) {
    getCaseSign();
    // }
  }, [clientIsLoggedIn, forceRender, getCaseSign]);

  useEffect(() => {
    if (completionReady) {
      setActiveStep(finalStepIndex);
      return;
    }

    setActiveStep(maxAccessibleStep);
  }, [completionReady, finalStepIndex, maxAccessibleStep]);

  const handleStepClick = (index) => {
    if (index <= maxAccessibleStep) {
      setActiveStep(index);
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleNextStep = () => {
    // Solo permitir avanzar hasta el ultimo step desbloqueado.
    if (activeStep < maxAccessibleStep) {
      setActiveStep((prev) => prev + 1);
    }
  };

  //  Estados
  // Mostrar loading SOLO cuando:
  // - el contexto aún carga
  // - o el cliente está logueado pero aún no cargó user/case
  if (
    clientIsLoading ||
    (clientIsLoggedIn && (!isUserLoaded || !isCaseLoaded))
  ) {
    return <LoadingPage isLoading />;
  }

  const isUnauthorized =
    clientIsLoggedIn &&
    dataCase &&
    user &&
    Number(dataCase.client_id) !== Number(user.id);

  if (isUnauthorized) {
    return <NotFoundPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "left",
        display: "flex",
      }}
    >
      <Fade in={bgLoaded} timeout={1000}>
        <img
          src={fidelitis2}
          alt="Fondo Login"
          className="fondoLogin"
          style={{ display: bgLoaded ? "block" : "none" }}
          onLoad={() => setBgLoaded(true)}
        />
      </Fade>

      {/* SI NO ESTÁ LOGUEADO → CARD OTP */}
      {!clientIsLoading && !clientIsLoggedIn && !dataCase?.length > 0 && (
        <Fade in={true} timeout={800}>
          <Box sx={pageContainer}>
            <Card
              sx={{
                ...glassCard,
                width: "100%",
                minWidth: { xs: "95%", sm: "98%", md: "800px" },
                p: { xs: 1.5, sm: 3 },
                mt: { xs: 0.5, sm: 4 },
              }}
            >
              {isCaseNumberLoading ? (
                <Box component="span" sx={expedienteNumberLoading}>
                  Cargando
                  <Box component="span" className="dot" />
                  <Box component="span" className="dot" />
                  <Box component="span" className="dot" />
                </Box>
              ) : (
                <>
                  <CardContent>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      mb={1}
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    >
                      {dataCase?.case_id
                        ? `EXPEDIENTE # ${dataCase.case_id}`
                        : " N/A"}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      mb={1}
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    >
                      Verificación de identidad
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Ingresa el código recibido en tu correo para continuar
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Typography
                      mt={3}
                      fontWeight={500}
                      sx={{
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        textAlign: "center",
                        fontWeight: 700,
                      }}
                    >
                      Código de verificación
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="center"
                      gap={{ xs: 0.7, sm: 1.5 }}
                      mt={2}
                      flexWrap="nowrap"
                    >
                      {otp.map((num, i) => (
                        <TextField
                          key={i}
                          id={`otp-${i}`}
                          value={num}
                          onChange={(e) => handleOtpChange(e.target.value, i)}
                          onKeyDown={(e) => handleOtpKeyDown(e, i)}
                          onPaste={(e) => handleOtpPaste(e, i)}
                          inputProps={{ maxLength: 1 }}
                          type="tel"
                          autoComplete="one-time-code"
                          sx={otpInput}
                        />
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: "space-between",
                      mt: 1,
                      gap: 1,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* $2y$10$vSmwX.tZ417Q.D1xx7IMWOSR58CIjcEoT2N.brAYVNwqDsWoETPJy */}
                    <Button
                      onClick={handleGoBack}
                      color="error"
                      variant="outlined"
                      sx={{
                        ...actionButton,
                        width: { xs: "100%", sm: "48%" },
                      }}
                    >
                      Volver
                    </Button>
                    <Button
                      onClick={handleResendCode}
                      startIcon={<Mail />}
                      variant="outlined"
                      sx={{
                        ...actionButton,
                        width: { xs: "100%", sm: "48%" },
                      }}
                    >
                      Reenviar código
                    </Button>
                    <Button
                      onClick={handleClientSignInformation}
                      endIcon={<ArrowForwardIos />}
                      variant="contained"
                      color="success"
                      sx={{
                        ...actionButton,
                        width: { xs: "100%", sm: "48%" },
                      }}
                    >
                      Confirmar
                    </Button>
                  </CardActions>
                </>
              )}
            </Card>
          </Box>
        </Fade>
      )}

      {/* SI ESTÁ LOGUEADO → CONTRATO */}
      {clientIsLoggedIn && !clientIsLoading && dataCase && (
        <>
          {!caseContract || !caseContract.contract ? (
            <LoadingPage isLoading={true} />
          ) : (
            <Fade in={true} timeout={800}>
              <div style={{ width: "100%" }}>
                <Box sx={sectionBox}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 1.5, sm: 0 }}
                  >
                    <Box textAlign="left" width={"stretch"}>
                      <Typography
                        variant="h4"
                        fontWeight={600}
                        mb={1}
                        sx={{
                          fontSize: {
                            xs: "1.4rem",
                            sm: "2rem",
                          },
                          maxWidth: "90%",
                        }}
                      >
                        ¡Hola {user?.name} {user?.surname}!
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                      >
                        Te invitamos a firmar la hoja de encargo
                      </Typography>
                    </Box>

                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={<Logout />}
                      onClick={cerrarSesion}
                      sx={{
                        ...actionButton,
                        width: { xs: "100%", sm: "auto" }, // ocupa todo el ancho en xs
                        maxWidth: { xs: "100%", sm: "250px" }, // límite en sm
                        whiteSpace: "nowrap", // evita salto de línea
                        overflow: "hidden", // oculta cualquier desborde
                        textOverflow: "ellipsis", // si se llegara a cortar, pone "..."
                        display: "flex", // asegura que ícono y texto estén alineados
                        justifyContent: "center", // centra el contenido en xs
                        alignItems: "center",
                      }}
                    >
                      Cerrar sesión
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    ...sectionBox,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 0.75, sm: 2, md: 4 },
                  }}
                >
                  {/* Flecha izquierda */}
                  <IconButton
                    onClick={handlePrevStep}
                    disabled={activeStep === 0}
                    sx={{
                      opacity: activeStep === 0 ? 0.3 : 1,
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>

                  {/* Stepper */}
                  <Box sx={{ flex: 1 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                      {steps.map((stepLabel, index) => (
                        <Step key={stepLabel} completed={activeStep > index}>
                          <StepLabel
                            onClick={() => handleStepClick(index)}
                            sx={{
                              cursor:
                                index <= maxAccessibleStep
                                  ? "pointer"
                                  : "not-allowed",
                              "& .MuiStepLabel-label": {
                                fontWeight: 600,
                                fontSize: { xs: "13px", sm: "16px" },
                              },
                              opacity: index <= maxAccessibleStep ? 1 : 0.5,
                            }}
                          >
                            {stepLabel}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {/* Flecha derecha */}
                  <IconButton
                    onClick={handleNextStep}
                    disabled={activeStep >= maxAccessibleStep}
                    sx={{
                      opacity: activeStep >= maxAccessibleStep ? 0.3 : 1,
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </Box>

                {activeStep === 0 && (
                  <Fade in={true} timeout={800}>
                    <Box
                      sx={{
                        ...sectionBox,
                        flexDirection: "column",
                      }}
                    >
                      <FirmaContent
                        rerender={forceRender}
                        viewMode="client-direct"
                        caseContract={caseContract}
                        acceptedContract={acceptedContract}
                        nextTask={nextTask}
                        flagAcceptReject={
                          canSign &&
                          dataCase?.flow?.status_name
                            ?.toLowerCase()
                            .includes("firm")
                        }
                        setUpdatedCase={setUpdatedCase}
                        updatedCase={updatedCase}
                        dataCase={dataCase}
                      />
                    </Box>
                  </Fade>
                )}
                {activeStep === 1 && canFillForm && hasFI && (
                  <Fade in={true} timeout={800}>
                    <Box sx={{ ...sectionBox, p: { xs: 1.25, sm: 3, md: 4 } }}>
                      <FormFI
                        caseId={case_id}
                        nextTask={nextTask}
                        dataCase={dataCase}
                        setUpdatedCase={setUpdatedCase}
                        updatedCase={updatedCase}
                        activeTask={activeTask}
                      />
                    </Box>
                  </Fade>
                )}
                {activeStep === finalStepIndex && (
                  <Fade in={true} timeout={800}>
                    <Box
                      sx={{
                        ...sectionBox,
                        flexDirection: "column",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="h5" fontWeight={700} align="left">
                        Proceso completado
                      </Typography>
                      <Typography
                        variant="body1"
                        align="left"
                        color="text.secondary"
                      >
                        Hemos completado tu proceso. Nos comunicaremos contigo
                        si necesitamos más información.
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </div>
            </Fade>
          )}
        </>
      )}
    </Box>
  );
};

export default ClientSign;
